import { Component, OnDestroy, OnInit } from '@angular/core'
import { Store, select } from '@ngrx/store'
import Validator from '@shared/validatorjs/src/validator'
import { Map } from 'immutable'
import { debounceTime, Subject, Subscription } from 'rxjs'
import FormTrait from 'src/app/traits/form.trait'
import {
  CreateBusinessCategoryRequest,
  CreateBusinessSubcategoryRequest,
  SetDefaultData,
  UpdateField,
} from '../business-categories.actions'
import { getCreateBusinessSubcategoryState } from '../business-categories.selectors'
import {
  FetchBusinessSubcategoryCalculationTypeRequest,
  FetchBusinessSubcategoryTypeRequest,
} from '@shared/shared.actions'
import {
  getBusinessSubcategoryCalculationTypesState,
  getBusinessSubcategoryTypesState,
} from '@shared/shared.select'
import { ActivatedRoute } from '@angular/router'
import { getRowValidations } from '@shared/shared.utils'
import { FetchTemplatesRequest } from '@containers/templates/templates.actions'
import { getTemplatesState } from '@containers/templates/templates.selectors'

@Component({
  selector: 'app-create-business-subcategory',
  templateUrl: './create-business-subcategory.component.html',
  styleUrls: ['./create-business-subcategory.component.css'],
})
export class CreateBusinessSubcategoryComponent
  extends FormTrait
  implements OnInit, OnDestroy
{
  private state$: Subscription
  public isLoading: boolean
  public types$: Subscription
  public types: any[]
  public templates$: Subscription
  public templates: any[]
  public calculationTypes$: Subscription
  public calculationTypes: any[]
  public calculationTypesBySubcategoryType: any[]
  public businessCategoryId: number
  public errors: any = {}
  public onTemplateAutocomplete$ = new Subject<string>()
  private templateAutocomplete$: Subscription
  public attrNames = {
    subCategoryType: 'subcategory type',
    calculationType: 'calculation type',
    rangeStart: 'range start',
    rangeEnd: 'range end',
    propertyType: 'property type',
    penaltyPercentage: 'penalty percentage',
    minTax: 'Min. Tax',
  }
  public isPropertyTypesOpen = false
  public typesTranslateKeys = []
  public typesTranslateDefaults = {}

  constructor(public store: Store<any>, private route: ActivatedRoute) {
    super()
  }

  ngOnInit() {
    this.businessCategoryId = parseInt(
      this.route.snapshot.paramMap.get('id'),
      10,
    )
    this.store.dispatch<any>(new SetDefaultData('createBusinessSubcategory'))
    this.state$ = this.store
      .pipe(select(getCreateBusinessSubcategoryState))
      .subscribe((state) => {
        this.state = state
        this.isLoading = state.get('isLoading')
        const errors = state.get('validationErrors').toJS()

        this.errors = getRowValidations(errors, this.attrNames)
      })

    this.calculationTypes$ = this.store
      .pipe(select(getBusinessSubcategoryCalculationTypesState))
      .subscribe((calculationTypes) => {
        this.calculationTypes = calculationTypes
      })

    this.types$ = this.store
      .pipe(select(getBusinessSubcategoryTypesState))
      .subscribe((types) => {
        this.types = types

        this.typesTranslateKeys = this.types.map((type) => type.trnKey)

        this.typesTranslateDefaults = this.types.reduce((prev, current) => {
          return { ...prev, [current.trnKey]: current.defaultName }
        }, {})
      })

    this.templates$ = this.store
      .pipe(select(getTemplatesState))
      .subscribe((templates) => {
        this.templates = templates.get('data').toJS()
      })

    this.templateAutocomplete$ = this.onTemplateAutocomplete$
      .pipe(debounceTime(500))
      .subscribe((keyword) => {
        if (keyword) {
          this.store.dispatch<any>(
            new FetchTemplatesRequest({
              filters: {
                or: [{ field: 'name', value: keyword }],
                matchAnd: [{ field: 'type', value: 'BL_TEMPLATE' }],
              },
              pageSize: 10,
              page: 0,
            }),
          )
        }
      })

    this.store.dispatch<any>(
      new FetchTemplatesRequest({
        filters: {
          or: [{ field: 'type', value: 'BL_TEMPLATE' }],
        },
        pageSize: 10,
        page: 0,
      }),
    )

    this.store.dispatch(new FetchBusinessSubcategoryTypeRequest())
    this.store.dispatch(new FetchBusinessSubcategoryCalculationTypeRequest())
  }

  ngOnDestroy() {
    this.state$.unsubscribe()
    this.types$.unsubscribe()
    this.calculationTypes$.unsubscribe()
    this.templateAutocomplete$.unsubscribe()
  }

  public handleFieldChange(
    e: any,
    name: null | string = null,
    row: null | number = null,
  ): void {
    if (row !== null) {
      this.store.dispatch(
        new UpdateField(
          name || e.target.name,
          name
            ? e
            : isNaN(e.target.value)
            ? e.target.value
            : parseFloat(e.target.value),
          `createBusinessSubcategory.data.logicSlots.${row}`,
        ),
      )

      if ((name || e.target.name) === 'calculationType') {
        const selected = this.calculationTypesBySubcategoryType.find(
          (type) => type.name === e.target.value,
        )

        if (selected) {
          this.store.dispatch(
            new UpdateField(
              'hasRange',
              selected.hasRange,
              `createBusinessSubcategory.data.logicSlots.${row}`,
            ),
          )
          this.store.dispatch(
            new UpdateField(
              'rangeStart',
              0,
              `createBusinessSubcategory.data.logicSlots.${row}`,
            ),
          )
          this.store.dispatch(
            new UpdateField(
              'rangeEnd',
              0,
              `createBusinessSubcategory.data.logicSlots.${row}`,
            ),
          )
        }
      }
    } else {
      this.store.dispatch(
        new UpdateField(
          name || e.target.name,
          name ? e : e.target.value,
          'createBusinessSubcategory.data',
        ),
      )

      this.store.dispatch(
        new UpdateField(
          'validationErrors',
          this.state.get('validationErrors').delete(name || e.target.name),
          'createBusinessSubcategory',
        ),
      )

      if (
        this.calculationTypes.length &&
        (name || e.target.name) === 'subCategoryType'
      ) {
        const selected = this.types.find((type) => type.name === e.target.value)

        if (selected) {
          const { allowedSlotTypes } = selected

          this.calculationTypesBySubcategoryType = this.calculationTypes.filter(
            (type) => allowedSlotTypes.includes(type.name),
          )

          this.clearUnsupportedCalcTypes()
        }
      }
    }
  }

  public clearUnsupportedCalcTypes() {
    const slots = (this.state.getIn(['data', 'logicSlots']) as any).toJS()
    const newData = slots.map((slot: any) => {
      if (
        !this.calculationTypesBySubcategoryType.includes(slot.calculationType)
      ) {
        return { ...slot, calculationType: null }
      }
      return slot
    })

    this.store.dispatch(
      new UpdateField('logicSlots', newData, `createBusinessSubcategory.data`),
    )
  }

  public rowDelete(row: number) {
    const list = this.state.getIn(['data', 'logicSlots']) as any

    this.store.dispatch(
      new UpdateField(
        'logicSlots',
        list.delete(row).toJS(),
        `createBusinessSubcategory.data`,
      ),
    )
  }

  public rowAdd() {
    const list = this.state.getIn(['data', 'logicSlots']) as any
    this.store.dispatch(
      new UpdateField(
        'logicSlots',
        list
          .push({
            name: '',
            calculationType: null,
            rangeStart: 0,
            rangeEnd: 0,
            value: 0,
          })
          .toJS(),
        `createBusinessSubcategory.data`,
      ),
    )
  }

  get isFixedType() {
    const typeName = this.state.getIn(['data', 'subCategoryType'])
    const isFixed = this.types?.find(
      (type) =>
        type.name === typeName &&
        type.allowedSlotTypes.includes('FIXED_MANUAL'),
    )
    return !!isFixed
  }

  private validation() {
    const data = this.state.get('data')
    const rules = {
      name: 'required',
      subCategoryType: 'required',
      logicSlots: 'array',
      penaltyPercentage: 'required|numeric',
      minTax: 'required|numeric',
      'logicSlots.*.name': 'required',
      'logicSlots.*.calculationType': 'required',
      'logicSlots.*.rangeStart': 'required|min:0',
      'logicSlots.*.rangeEnd': 'required|min:0',
      'logicSlots.*.value': 'required|min:0',
    }

    if (!this.isFixedType) {
      rules['propertyType'] = 'required'
    }

    const validator = new Validator(
      {
        name: data.get('name'),
        propertyType: data.get('propertyType'),
        subCategoryType: data.get('subCategoryType'),
        penaltyPercentage: data.get('penaltyPercentage'),
        minTax: data.get('minTax'),
        logicSlots: data.get('logicSlots').toJS(),
      },
      rules,
    )

    validator.setAttributeNames(this.attrNames)

    return validator
  }

  public onSubmit(e: Event): void {
    e.preventDefault()
    const validation = this.validation()
    const payload = this.state.get('data').toJS()

    if (validation.passes()) {
      this.store.dispatch(
        new CreateBusinessSubcategoryRequest(this.businessCategoryId, {
          ...payload,
          penaltyPercentage: parseFloat(payload.penaltyPercentage),
          minTax: parseFloat(payload.minTax),
        }),
      )
    } else {
      this.store.dispatch<any>(
        new UpdateField(
          'validationErrors',
          validation.errors.errors,
          'createBusinessSubcategory',
        ),
      )
    }
  }
}
